import { useMemo, useState, useCallback } from 'react';
import { ProjectEditorApiService } from '~/services';

/**
 * Immediate-CRUD orchestration for a submitted estimate's clients & contacts.
 *
 * Unlike the estimate save (which the Companies slice bundles), project subordinates persist
 * immediately — each add/remove/change is its own API call, like estimate items. After every
 * mutation we refetch estimate-details so the editor reflects server truth (including the new row
 * ids the read VM now surfaces). `reloadKey` bumps on every reload so callers can remount the
 * grids/editors to pick up the fresh rows.
 *
 * @param {number} projectId
 * @param {Object} viewModel - the initial estimate-details VM (landlord / construction / billing)
 */
export function useProjectClients(projectId, viewModel) {
  const apiService = useMemo(() => new ProjectEditorApiService(), []);

  const [landlord, setLandlord] = useState(viewModel?.landlord ?? null);
  const [constructionClients, setConstructionClients] = useState(viewModel?.constructionClients ?? null);
  const [billingClients, setBillingClients] = useState(viewModel?.billingClients ?? null);
  const [estimatingClients, setEstimatingClients] = useState(viewModel?.estimatingClients ?? null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(async () => {
    const vm = await apiService.getEstimateDetails(projectId);
    setLandlord(vm?.landlord ?? null);
    setConstructionClients(vm?.constructionClients ?? null);
    setBillingClients(vm?.billingClients ?? null);
    setEstimatingClients(vm?.estimatingClients ?? null);
    setReloadKey((k) => k + 1);
  }, [apiService, projectId]);

  // Run a write, then always reload — on failure the toast has already fired (fetchApiOrThrow) and
  // the reload restores the grid to server truth, reverting any optimistic edit.
  const runThenReload = useCallback(async (write) => {
    try {
      await write();
    } finally {
      await reload();
    }
  }, [reload]);

  const setLandlordCompany = useCallback(
    (companyId) => runThenReload(() => apiService.setLandlordClient(projectId, { companyId })),
    [apiService, projectId, runThenReload]
  );

  const addClient = useCallback(
    (relationshipId, values) =>
      runThenReload(() => apiService.createProjectClient(projectId, { ...values, relationshipId })),
    [apiService, projectId, runThenReload]
  );

  const updateClient = useCallback(
    (projectClientId, changes) =>
      runThenReload(() => apiService.updateProjectClient(projectId, projectClientId, changes)),
    [apiService, projectId, runThenReload]
  );

  const deleteClient = useCallback(
    (projectClientId) =>
      runThenReload(() => apiService.deleteProjectClient(projectId, projectClientId)),
    [apiService, projectId, runThenReload]
  );

  const addContact = useCallback(
    (projectClientId, values) =>
      runThenReload(() => apiService.createProjectContact(projectId, projectClientId, values)),
    [apiService, projectId, runThenReload]
  );

  const removeContact = useCallback(
    (projectClientId, projectContactId) =>
      runThenReload(() => apiService.deleteProjectContact(projectId, projectClientId, projectContactId)),
    [apiService, projectId, runThenReload]
  );

  const changeContactType = useCallback(
    (projectClientId, projectContactId, landlordContactTypeId) =>
      runThenReload(() =>
        apiService.updateProjectContact(projectId, projectClientId, projectContactId, {
          landlordContactTypeId,
        })
      ),
    [apiService, projectId, runThenReload]
  );

  return {
    landlord,
    constructionClients,
    billingClients,
    estimatingClients,
    reloadKey,
    setLandlordCompany,
    addClient,
    updateClient,
    deleteClient,
    addContact,
    removeContact,
    changeContactType,
  };
}
